// Document — the editor's content model.
//
// Holds the markdown source plus a parsed list of blocks (one per line
// or per fenced code block). render() does a longest-prefix /
// longest-suffix diff against the previous block list and mutates only
// the changed blocks in the live DOM, instead of rewriting innerHTML on
// every keystroke. This is what makes the editor stay responsive on
// long documents.

import { parse } from "./parser.js"
import { SelectionTracker } from "./selection.js"
import { History } from "./history.js"

function dispatch(target, name, detail = null, cancelable = false) {
  return target.dispatchEvent(new CustomEvent(name, { bubbles: true, detail, cancelable }))
}

export class Document {
  constructor(content, element) {
    this.content = content
    this.element = element
    this.blocks = []
    this.wbrAnchor = null
    this.selection = new SelectionTracker(this)
    this.history = new History()
  }

  select(start, end) {
    this.selection.select({ start, end })
  }

  insertText(text) {
    const { start, end } = this.currentSelection
    this.replaceText(text, start, end)
  }

  replaceText(text, from, to) {
    const head = this.content.slice(0, from)
    const tail = this.content.slice(to)
    const next = `${head}${text}${tail}`
    const cursor = head.length + text.length
    this.recordHistory()
    this.commit(next, { start: cursor, end: cursor })
  }

  insertParagraph(from, to) {
    this.select(from, to)
    if (this.selection.isBulletList) {
      this.continueBulletList()
    } else if (this.selection.isNumberList) {
      this.continueNumberList()
    } else {
      this.replaceText("\n", from, to)
    }
  }

  insertLink(label, url, prefix = "") {
    const { start, end } = this.currentSelection
    const inserted = ` ${prefix}[${label}](${url}) `
    this.replaceText(inserted, start, end)
    const cursor = start + inserted.length
    this.selection.select({ start: cursor, end: cursor })
  }

  insertFile(name, url, mimetype) {
    if (mimetype.startsWith("image/")) this.insertImage(name, url)
    else this.insertLink(name, url)
  }

  insertImage(name, url) {
    this.insertLink(name, url, "!")
  }

  deleteText(from, to) {
    this.replaceText("", Math.max(from, 0), to)
  }

  deleteCurrentLine() {
    const { start, end } = this.currentLine
    this.deleteText(start, end)
  }

  toggleBold() {
    this.toggleWrap(this.selection.isBold, "**")
  }

  toggleItalic() {
    this.toggleWrap(this.selection.isItalic, "_")
  }

  toggleStrikethrough() {
    this.toggleWrap(this.selection.isStrikethrough, "~~")
  }

  toggleCode() {
    const [open, close] = this.selection.isMultiline ? ["```\n", "\n```"] : ["`", "`"]
    this.toggleWrap(this.selection.isCode, open, close)
  }

  toggleLink() {
    if (this.selection.isLink) this.removeLink()
    else this.addLink()
  }

  toggleQuote() {
    if (this.selection.isQuote) this.removeLinePrefix("> ")
    else this.addLinePrefix("> ")
  }

  toggleBulletList() {
    if (this.selection.isBulletList) this.removeLinePrefix("- ")
    else this.addLinePrefix("- ")
  }

  toggleNumberList() {
    if (this.selection.isNumberList) {
      this.removeLinePrefix(/\d+\. /)
    } else {
      this.addLinePrefix(`${this.previousNumberListNumber + 1}. `)
    }
  }

  undo() {
    const { content, start, end } = this.history.undo(this.snapshot)
    if (typeof content === "string") this.commit(content, { start, end })
  }

  redo() {
    const { content, start, end } = this.history.redo(this.snapshot)
    if (typeof content === "string") this.commit(content, { start, end })
  }

  // render() is the diff path. parse() builds a fresh block list; we
  // walk the longest matching prefix and suffix against the previous
  // list and mutate only the middle. lineEls for unchanged blocks stay
  // in the DOM untouched, which preserves their internal text nodes —
  // important for caret/selection stability.
  render() {
    const next = parse(this.content)
    const previous = this.blocks
    const root = this.contentElement

    if (!this.wbrAnchor) {
      this.wbrAnchor = document.createElement("wbr")
      root.replaceChildren(this.wbrAnchor)
    }

    let prefix = 0
    while (
      prefix < previous.length &&
      prefix < next.length &&
      previous[prefix].source === next[prefix].source
    ) {
      next[prefix].lineEl = previous[prefix].lineEl
      prefix++
    }

    let suffix = 0
    const oldRemaining = previous.length - prefix
    const newRemaining = next.length - prefix
    while (
      suffix < oldRemaining &&
      suffix < newRemaining &&
      previous[previous.length - 1 - suffix].source === next[next.length - 1 - suffix].source
    ) {
      next[next.length - 1 - suffix].lineEl = previous[previous.length - 1 - suffix].lineEl
      suffix++
    }

    for (let i = prefix; i < previous.length - suffix; i++) {
      previous[i].lineEl?.remove()
    }

    const anchor = suffix > 0 ? next[next.length - suffix].lineEl : this.wbrAnchor
    for (let i = prefix; i < next.length - suffix; i++) {
      const block = next[i]
      block.lineEl = buildBlockEl(block.html)
      root.insertBefore(block.lineEl, anchor)
    }

    this.blocks = next
  }

  get isEmpty() {
    return !this.content.trim()
  }

  get contentElement() {
    return this.element.contentWrapper
  }

  get currentSelection() {
    return this.selection.current()
  }

  get currentLine() {
    return this.selection.currentLine()
  }

  recordHistory() {
    this.history.add(this.snapshot)
  }

  // The single choke point for every content change — typing, delete, paste,
  // drop, toolbar action, undo/redo, and the IME compositionend path all land
  // here. So this is where the element's hidden input is refreshed and where
  // the change is announced.
  //
  // `input` is dispatched by hand because InputHandler cancels `beforeinput`,
  // which suppresses the browser's own `input` — without this, house-md would
  // be the one form control on the page that never fires it. Note that during
  // IME composition we deliberately do NOT cancel `beforeinput`, so the
  // browser fires its own `input` while composing and compositionend -> commit
  // fires ours on top: a consumer sees one extra event per composition. Fine
  // for idempotent handlers (recompute from the live value); something that
  // counts events or does non-idempotent work per event should bind
  // `house-md:change` instead, which fires exactly once per commit.
  commit(nextContent, nextSelection) {
    const previousContent = this.content
    this.content = nextContent
    this.element.writeFormValue(nextContent)
    this.render()
    this.selection.select(nextSelection)
    dispatch(this.element, "house-md:change", { previousContent, newContent: nextContent })
    this.element.dispatchEvent(new InputEvent("input", { bubbles: true, composed: true }))
  }

  get snapshot() {
    const { start, end } = this.currentSelection
    return { content: this.content, start, end }
  }

  get currentLineContent() {
    return this.currentLine.content
  }

  get previousNumberListNumber() {
    const match = this.selection.previousLine().content.match(/^(\d+). /)
    return match ? parseInt(match[1]) : 0
  }

  toggleWrap(active, open, close = open) {
    if (active) this.unwrap(open, close)
    else this.wrap(open, close)
  }

  wrap(open, close = open) {
    const { start, end, selection } = this.currentSelection
    const wrapped = `${open}${selection}${close}`
    this.replaceText(wrapped, start, end)
    this.selection.select({ start, end: start + selection.length + open.length + close.length })
  }

  unwrap(open, close = open) {
    const { start, end, selection } = this.currentSelection
    if (!selection.startsWith(open) || !selection.endsWith(close)) return
    this.replaceText("", end - close.length, end)
    this.replaceText("", start, start + open.length)
    this.selection.select({ start, end: end - open.length - close.length })
  }

  addLink() {
    const { start, end, selection } = this.currentSelection
    const empty = this.selection.isEmpty
    const label = empty ? "title" : selection
    this.replaceText(`[${label}](url)`, start, end)
    if (empty) {
      this.selection.select({ start: start + 1, end: start + label.length + 1 })
    } else {
      this.selection.select({ start: start + selection.length + 3, end: start + selection.length + 6 })
    }
  }

  removeLink() {
    const { start, end, selection } = this.currentSelection
    const stripped = selection.replace(/\[(.*)\]\(.*\)/, "$1")
    this.replaceText(stripped, start, end)
  }

  // Bullet-list Enter — combines the previous "two replaceText" path
  // (insert "\n", then insert "- ") into a single mutation so the
  // diff render runs once per Enter instead of twice.
  continueBulletList() {
    if (this.currentLineContent.match(/^-\s+$/)) {
      this.deleteCurrentLine()
      return
    }
    const at = this.currentSelection.end
    this.replaceText("\n- ", at, at)
  }

  continueNumberList() {
    if (this.currentLineContent.match(/^\d+\.\s+$/)) {
      this.deleteCurrentLine()
      return
    }
    const at = this.currentSelection.end
    this.replaceText(`\n${this.previousNumberListNumber + 1}. `, at, at)
  }

  addLinePrefix(prefix) {
    const { start, end } = this.currentSelection
    const line = this.currentLine
    this.replaceText(prefix, line.start, line.start)
    this.selection.select({ start: start + prefix.length, end: end + prefix.length })
  }

  removeLinePrefix(prefix) {
    const { start, end } = this.currentSelection
    const line = this.currentLine
    let resolved = prefix
    if (resolved instanceof RegExp) {
      const match = line.content.match(resolved)
      if (!match) return
      resolved = match[0]
    }
    if (!line.content.startsWith(resolved)) return
    this.deleteText(line.start, line.start + resolved.length)
    this.selection.select({ start: start - resolved.length, end: end - resolved.length })
  }
}

function buildBlockEl(html) {
  const el = document.createElement("span")
  el.setAttribute("data-block", "")
  el.innerHTML = html
  return el
}
